import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "dayjs/locale/es";
import { notification } from "../../utils/Notifications.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import S3Service from "../../services/s3.service.ts";
import {
  getIncapacityById,
  approveIncapacity,
  getIncapacitiesByPartner,
} from "../../services/incapacities.service.ts";
import { getScheduleByPartner } from "../../services/branch-schedule.service.ts";
import IncapacityFormEditFiles from "../../components/incapacities/IncapacityFormEditFiles.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import { Panel, PanelHeader } from "../../components/panel/panel.jsx";
import Button from "../../components/Button.tsx";
import Loader from "../../components/Loader.tsx";
import moment from "moment";
import "moment/locale/es";
import { DatePicker, ConfigProvider } from "antd";
import esES from "antd/es/locale/es_ES";

// Configurar dayjs
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("es");
moment.locale("es");

const ApprovedIncapacity = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  useAppSettings();
  const [loading, setLoading] = useState(false);
  const [incapacity, setIncapacity] = useState(null);
  const [reloadFiles, setReloadFiles] = useState(false);
  const [profileUrl, setProfileUrl] = useState("");
  const [partnerSchedule, setPartnerSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [partnerHistory, setPartnerHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const validationSchema = Yup.object().shape({
    approved: Yup.boolean().required("Debe seleccionar una opción"),
    reviewerComment: Yup.string().when("approved", {
      is: false,
      then: (schema) =>
        schema.required("El comentario es obligatorio al rechazar"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      description: "",
      approved: false,
      daysOff: 1,
      startDate: "",
      endDate: "",
      requireTacticRelease: false,
      reviewerComment: "",
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const incapacityData = {
          description: values.description,
          approved: values.approved,
          daysOff: values.daysOff,
          startDate: values.startDate
            ? dayjs(values.startDate).unix()
            : undefined,
          endDate: values.endDate ? dayjs(values.endDate).unix() : undefined,
          isRejected: !values.approved,
          requireTacticRelease: values.requireTacticRelease,
          reviewerComment: values.reviewerComment,
        };

        console.log("Enviando datos de incapacidad:", incapacityData);

        // Aprobar la incapacidad
        await approveIncapacity(Number(id), incapacityData);

        notification(
          "Éxito",
          `Incapacidad ${
            values.approved ? "aprobada" : "rechazada"
          } correctamente`,
          "success"
        );
        navigate(-1);
      } catch (error) {
        console.error("Error al procesar la incapacidad:", error);
        notification("Error", "No se pudo procesar la incapacidad", "danger");
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchIncapacity = async () => {
    setLoading(true);
    try {
      const response = await getIncapacityById(Number(id));
      setIncapacity(response);

      if (response.partner?.person?.curp) {
        try {
          const profileUrlResponse = await S3Service.getFile(
            "PERFIL_" + response.partner.person.curp
          );
          setProfileUrl(profileUrlResponse.url);
        } catch (error) {
          console.error("Error al obtener foto de perfil:", error);
          setProfileUrl("/assets/img/avatar.jpg");
        }
      }

      formik.setValues({
        description: response.description || "",
        approved: response.approved || false,
        daysOff: calculateDaysOff(response.startDate, response.endDate),
        startDate: response.startDate
          ? dayjs.unix(response.startDate).format("YYYY-MM-DD")
          : "",
        endDate: response.endDate
          ? dayjs.unix(response.endDate).format("YYYY-MM-DD")
          : "",
        requireTacticRelease: response.requireTacticRelease || false,
        reviewerComment: response.reviewerComment || "",
      });
    } catch (error) {
      console.error("Error al cargar la incapacidad:", error);
      notification("Error", "No se pudo cargar la incapacidad", "danger");
    } finally {
      setLoading(false);
    }
  };

  const fetchPartnerSchedule = async () => {
    if (
      !incapacity?.partner?.id ||
      !incapacity?.startDate ||
      !incapacity?.endDate
    )
      return;

    setLoadingSchedule(true);
    try {
      const startDate = dayjs.unix(incapacity.startDate);
      const endDate = dayjs.unix(incapacity.endDate);

      const allPartnerSchedules = await getScheduleByPartner(
        incapacity.partner.id
      );

      const schedulesInRange =
        allPartnerSchedules.records?.filter((schedule) => {
          if (!schedule.exactDate) return false;

          const scheduleDate = dayjs(schedule.exactDate);
          const isInRange =
            scheduleDate.isSameOrAfter(startDate, "day") &&
            scheduleDate.isSameOrBefore(endDate, "day");

          return isInRange;
        }) || [];

      setPartnerSchedule(schedulesInRange);
    } catch (error) {
      console.error("Error al cargar el horario del partner:", error);
      setPartnerSchedule(null);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const loadPartnerHistory = async (partnerId: number) => {
    try {
      setLoadingHistory(true);
      const history = await getIncapacitiesByPartner(partnerId);
      // Ordenar por fecha más reciente (inicio de incapacidad o creación)
      const sortedHistory = (history || []).sort((a, b) => {
        const dateA = a.startDate
          ? dayjs.unix(a.startDate)
          : a.createdAt
          ? dayjs.unix(a.createdAt)
          : dayjs(0);
        const dateB = b.startDate
          ? dayjs.unix(b.startDate)
          : b.createdAt
          ? dayjs.unix(b.createdAt)
          : dayjs(0);
        return dateB.diff(dateA);
      });
      setPartnerHistory(sortedHistory);
    } catch (error) {
      console.error("Error loading partner history:", error);
      setPartnerHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchIncapacity();
  }, [id]);

  useEffect(() => {
    if (incapacity) {
      fetchPartnerSchedule();
      if (incapacity.partner?.id) {
        loadPartnerHistory(incapacity.partner.id);
      }
    }
  }, [incapacity]);

  const handleReloadFiles = () => {
    setReloadFiles(!reloadFiles);
    fetchIncapacity();
  };

  const getIncapacityStatus = () => {
    if (!incapacity) return "";
    if (incapacity.approved) return "APROBADO";
    if (incapacity.isRejected) return "RECHAZADO";
    return "PENDIENTE";
  };

  const getStatusColor = () => {
    if (!incapacity) return "bg-secondary";
    if (incapacity.approved) return "bg-success";
    if (incapacity.isRejected) return "bg-danger";
    return "bg-warning";
  };

  const getHistoryStatusColor = (incapacityItem: any) => {
    if (incapacityItem.approved) return "bg-success";
    if (incapacityItem.isRejected) return "bg-danger";
    return "bg-warning";
  };

  const getHistoryStatus = (incapacityItem: any) => {
    if (incapacityItem.approved) return "APROBADO";
    if (incapacityItem.isRejected) return "RECHAZADO";
    return "PENDIENTE";
  };

  const isAlreadyProcessed =
    incapacity && (incapacity.approved || incapacity.isRejected);

  const formatIncapacityDate = (date: string) => {
    if (!date) return "-";
    return moment(date).format("dddd, D [de] MMMM [de] YYYY");
  };

  const formatDateRange = (startDate: number, endDate: number): string => {
    if (!startDate || !endDate) return "-";
    const start = moment.unix(startDate).format("DD/MM/YYYY");
    const end = moment.unix(endDate).format("DD/MM/YYYY");
    return `${start} - ${end}`;
  };

  const formatShortDate = (timestamp: number): string => {
    if (!timestamp) return "-";
    return moment.unix(timestamp).format("ddd DD MMM YYYY");
  };

  const calculateDaysOff = (startDate: number, endDate: number): number => {
    if (!startDate || !endDate) return 0;
    const start = moment.unix(startDate);
    const end = moment.unix(endDate);
    return end.diff(start, "days") + 1; // +1 para incluir el día inicial
  };

  const formatScheduleTime = (time: string) => {
    if (!time) return "-";
    return time.split(" - ")[0] || time;
  };

  const formatScheduleDate = (date: string) => {
    if (!date) return "-";
    return moment(date).format("dddd, D [de] MMMM [de] YYYY");
  };

  if (!incapacity) {
    return (
      <Layout>
        <Loader isLoading={loading} />
      </Layout>
    );
  }

  return (
    <Layout>
      <Title
        baseTitle="Revisar incapacidad"
        basePath={`/colaboradores/aprobar-incapacidad/${id}`}
        title="Revisar y procesar incapacidad"
      />

      <div className="m-4">
        <div className="row">
          <div className="col-lg-4 mb-3">
            {incapacity?.partner && (
              <>
              {/* Tabs: Información / Historial */}
              <div
                className="d-flex shadow-sm mb-3 border"
                style={{
                  borderRadius: "0.5rem",
                  overflow: "hidden",
                  backgroundColor: "#ffffff",
                  borderColor: "#e5e7eb",
                }}
              >
                <button
                  type="button"
                  className="flex-fill border-0 py-2 px-3 d-flex align-items-center justify-content-center small fw-semibold"
                  style={{
                    backgroundColor:
                      activeTab === "info" ? "#50B7C0" : "#ffffff",
                    color: activeTab === "info" ? "#ffffff" : "#4B5563",
                    borderRadius: "0.4rem 0 0 0.4rem",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                  }}
                  onClick={() => setActiveTab("info")}
                >
                  <i
                    className="fas fa-user me-1"
                    style={{ opacity: activeTab === "info" ? 1 : 0.7 }}
                  ></i>
                  Información
                </button>
                <button
                  type="button"
                  className="flex-fill border-0 py-2 px-3 d-flex align-items-center justify-content-center small fw-semibold"
                  style={{
                    backgroundColor:
                      activeTab === "history" ? "#50B7C0" : "#ffffff",
                    color: activeTab === "history" ? "#ffffff" : "#4B5563",
                    borderRadius: "0 0.4rem 0.4rem 0",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                  }}
                  onClick={() => setActiveTab("history")}
                >
                  <i
                    className="fas fa-history me-1"
                    style={{ opacity: activeTab === "history" ? 1 : 0.7 }}
                  ></i>
                  Historial
                </button>
              </div>

              {/* Contenido de la columna izquierda */}
              {activeTab === "info" && (
                <>
                  {/* Card 1: Información del Chilito */}
                  <div
                    className="card border-0 shadow-sm overflow-hidden mb-3"
                    style={{ borderRadius: "1rem" }}
                  >
                    <div
                      className="py-2 px-3 text-white d-flex align-items-center"
                      style={{ backgroundColor: "#50B7C0" }}
                    >
                      <i className="fas fa-user me-2"></i>
                      <h6 className="mb-0 fw-semibold">Información del Chilito</h6>
                    </div>
                    <div className="card-body p-4">
                      <div className="text-center mb-4">
                        <img
                          src={profileUrl || "/assets/img/avatar.jpg"}
                          alt="Foto de perfil"
                          className="rounded-circle shadow"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            border: "3px solid #e5e7eb",
                          }}
                          onError={(e) => {
                            e.target.src = "/assets/img/avatar.jpg";
                          }}
                        />
                        <h5 className="mt-3 mb-0 fw-bold text-dark">
                          {toCamelCase(
                            `${incapacity.partner.person?.firstName} ${incapacity.partner.person?.lastName} ${incapacity.partner.person?.secondLastName}`
                          )}
                        </h5>
                      </div>
                      <div className="border-top pt-3">
                        <div className="d-flex align-items-center mb-3">
                          <span className="text-muted small me-2" style={{ minWidth: "80px" }}>
                            <i
                              className="fas fa-briefcase me-1"
                              style={{ color: "#50B7C0" }}
                            ></i>
                            Puesto
                          </span>
                          <span className="fw-semibold text-dark small">
                            {incapacity.partner.candidate?.vacancy?.position?.name || "—"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center mb-3">
                          <span className="text-muted small me-2" style={{ minWidth: "80px" }}>
                            <i
                              className="fas fa-building me-1"
                              style={{ color: "#50B7C0" }}
                            ></i>
                            Sucursal
                          </span>
                          <span className="fw-semibold text-dark small">
                            {incapacity.partner.candidate?.branch?.name || "—"}
                          </span>
                        </div>
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pt-2 border-top">
                          <div className="d-flex align-items-center">
                            <i className="fas fa-id-card me-2 text-muted small"></i>
                            <span className="small text-dark fw-medium">
                              NSS {incapacity.partner.person?.nss || "No registrado"}
                            </span>
                          </div>
                          <span
                            className="badge small fw-normal"
                            style={{
                              backgroundColor: incapacity.partner.hasChiliLifeInsurance ? "#d1fae5" : "#f3f4f6",
                              color: incapacity.partner.hasChiliLifeInsurance ? "#065f46" : "#4b5563",
                              borderRadius: "999px",
                              padding: "0.35rem 0.75rem",
                            }}
                          >
                            Seguro Chili: {incapacity.partner.hasChiliLifeInsurance ? "Sí" : "No"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Card 2: Período de Incapacidad */}
                  <div
                    className="card border-0 shadow-sm overflow-hidden mb-3"
                    style={{ borderRadius: "1rem" }}
                  >
                    <div className="card-body p-4">
                      <h6 className="mb-3 text-dark d-flex align-items-center">
                        <i
                          className="fas fa-calendar-alt me-2"
                          style={{ color: "#50B7C0" }}
                        ></i>
                        Período de Incapacidad
                      </h6>
                      <div className="text-center mb-3">
                        <div className="text-muted small mb-1">
                          Días de incapacidad
                        </div>
                        <div
                          style={{
                            fontSize: "1.8rem",
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {calculateDaysOff(
                            incapacity.startDate,
                            incapacity.endDate
                          )}
                        </div>
                        <div className="text-muted small">días solicitados</div>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        <div
                          className="d-flex align-items-center justify-content-between p-2 rounded-3"
                          style={{ backgroundColor: "#F3F4F6" }}
                        >
                          <div className="d-flex align-items-center">
                            <span
                              className="badge bg-light text-dark me-2"
                              style={{ borderRadius: "999px" }}
                            >
                              Inicio
                            </span>
                            <span className="small text-dark">
                              {formatShortDate(incapacity.startDate)}
                            </span>
                          </div>
                        </div>
                        <div
                          className="d-flex align-items-center justify-content-between p-2 rounded-3"
                          style={{ backgroundColor: "#F3F4F6" }}
                        >
                          <div className="d-flex align-items-center">
                            <span
                              className="badge bg-light text-dark me-2"
                              style={{ borderRadius: "999px" }}
                            >
                              Fin
                            </span>
                            <span className="small text-dark">
                              {formatShortDate(incapacity.endDate)}
                            </span>
                          </div>
                        </div>
                       
                      </div>
                    </div>
                  </div>

                  {/* Card 3: Horarios durante el período */}
                  <div
                    className="card border-0 shadow-sm overflow-hidden mb-3"
                    style={{ borderRadius: "1rem" }}
                  >
                    <div className="card-body p-4">
                      <h6 className="mb-3 text-dark d-flex align-items-center">
                        <i className="fas fa-clock me-2 text-amber"></i>
                        Horarios durante el período
                      </h6>

                      {loadingSchedule ? (
                        <div className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-secondary" role="status">
                            <span className="visually-hidden">Cargando horarios...</span>
                          </div>
                          <div className="text-muted small mt-2">Verificando horarios...</div>
                        </div>
                      ) : partnerSchedule && partnerSchedule.length > 0 ? (
                        <div>
                          <div
                            className="p-3 rounded-3 small mb-3"
                            style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
                          >
                            <i className="fas fa-exclamation-triangle me-2"></i>
                            <strong>Atención:</strong> El colaborador tiene{" "}
                            {partnerSchedule.length} horario
                            {partnerSchedule.length > 1 ? "s" : ""} asignado
                            {partnerSchedule.length > 1 ? "s" : ""} durante este período.
                          </div>
                          <div
                            className="rounded-3 overflow-auto"
                            style={{
                              maxHeight: "220px",
                              border: "1px solid #e5e7eb",
                              backgroundColor: "#f9fafb",
                            }}
                          >
                            <div className="p-3">
                              {partnerSchedule.map((schedule, index) => (
                                <div
                                  key={index}
                                  className="bg-white rounded-3 p-3 mb-2 shadow-sm border-start border-3 border-teal"
                                  style={{ borderLeftColor: "#50B7C0" }}
                                >
                                  <div className="row align-items-center small">
                                    <div className="col-12 col-md-6 mb-2 mb-md-0">
                                      <div className="fw-semibold text-dark">
                                        {schedule.positionName || "Sin posición"}
                                      </div>
                                      <div className="text-muted">{schedule.branch?.name || "Sin sucursal"}</div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                      <div className="text-muted small">Fecha</div>
                                      <div className="text-dark">{formatScheduleDate(schedule.exactDate)}</div>
                                    </div>
                                    <div className="col-6 col-md-3">
                                      <div className="text-muted small">Horario</div>
                                      <div className="text-dark">
                                        {formatScheduleTime(schedule.startTime)} – {formatScheduleTime(schedule.endTime)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="p-3 rounded-3 small"
                          style={{ backgroundColor: "#d1fae5", color: "#065f46" }}
                        >
                          <i className="fas fa-check-circle me-2"></i>
                          <strong>Sin conflictos:</strong> El colaborador no tiene horarios asignados durante este período.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Historial Tab */}
              {activeTab === "history" && (
                <div
                  className="card border-0 shadow-sm overflow-hidden mb-3"
                  style={{ borderRadius: "1rem" }}
                >
                  <div
                    className="py-2 px-3 text-white d-flex align-items-center"
                    style={{ backgroundColor: "#50B7C0" }}
                  >
                    <i className="fas fa-history me-2"></i>
                    <h6 className="mb-0 fw-semibold">Historial de incapacidades</h6>
                  </div>
                  <div className="card-body p-4">
                    {loadingHistory ? (
                      <div className="d-flex flex-column align-items-center py-4">
                        <div className="spinner-border text-secondary" role="status">
                          <span className="visually-hidden">Cargando...</span>
                        </div>
                        <div className="text-muted small mt-2">Cargando historial...</div>
                      </div>
                    ) : partnerHistory.length > 0 ? (
                      <div className="overflow-auto" style={{ maxHeight: "400px" }}>
                        {partnerHistory.map((item: any, index: number) => (
                          <div
                            key={item.id || index}
                            className="card mb-2 border-0 shadow-sm"
                            style={{ borderRadius: "0.75rem", minHeight: "80px" }}
                          >
                            <div className="card-body p-3">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div className="fw-semibold text-dark small">
                                  {item.description
                                    ? item.description.length > 30
                                      ? item.description.substring(0, 30) + "..."
                                      : item.description
                                    : "Sin descripción"}
                                </div>
                                <span className={`badge ${getHistoryStatusColor(item)}`} style={{ borderRadius: "999px" }}>
                                  {getHistoryStatus(item)}
                                </span>
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-calendar me-1"></i>
                                {item.startDate && item.endDate
                                  ? formatDateRange(item.startDate, item.endDate)
                                  : "-"}
                              </div>
                              <div className="text-muted small mb-1">
                                <i className="fas fa-clock me-1"></i>
                                {item.startDate && item.endDate
                                  ? calculateDaysOff(item.startDate, item.endDate)
                                  : 0}{" "}
                                día
                                {item.startDate && item.endDate && calculateDaysOff(item.startDate, item.endDate) > 1 ? "s" : "s"}
                              </div>
                              {item.reviewerComment && (
                                <div className="text-muted small">
                                  <i className="fas fa-comment me-1"></i>
                                  {item.reviewerComment.length > 40
                                    ? item.reviewerComment.substring(0, 40) + "..."
                                    : item.reviewerComment}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="fas fa-inbox fa-2x mb-2"></i>
                        <div>No hay historial de incapacidades</div>
                        <small>Este colaborador no tiene registros previos</small>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="col-lg-8">
          <div
            className="card border-0 shadow-sm mb-4"
            style={{ borderRadius: "1rem", overflow: "hidden" }}
          >
            <Panel>
              <PanelHeader noButton={true} className="bg-azul">
                Datos generales de la incapacidad
              </PanelHeader>
              <div className="p-4">
                <ConfigProvider locale={esES}>
                  <div className="row">
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Descripción</label>
                      <textarea
                        className="form-control"
                        rows="4"
                        value={incapacity.description || ""}
                        disabled
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Rango de fechas</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formatDateRange(
                          incapacity.startDate,
                          incapacity.endDate
                        )}
                        disabled
                      />
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label">Días de Incapacidad</label>
                      <input
                        type="number"
                        className="form-control"
                        value={calculateDaysOff(
                          incapacity.startDate,
                          incapacity.endDate
                        )}
                        disabled
                      />
                    </div>

                    {!isAlreadyProcessed && (
                      <div className="col-md-12 mb-3">
                        <label className="form-label">Decisión *</label>
                        <div className="mt-2">
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="true"
                              className="form-check-input"
                              id="approve"
                              checked={formik.values.approved === true}
                              onChange={() =>
                                formik.setFieldValue("approved", true)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="approve"
                            >
                              Aprobar
                            </label>
                          </div>
                          <div className="form-check form-check-inline">
                            <input
                              type="radio"
                              name="approved"
                              value="false"
                              className="form-check-input"
                              id="reject"
                              checked={formik.values.approved === false}
                              onChange={() =>
                                formik.setFieldValue("approved", false)
                              }
                            />
                            <label
                              className="form-check-label"
                              htmlFor="reject"
                            >
                              Rechazar
                            </label>
                          </div>
                        </div>
                        {formik.touched.approved && formik.errors.approved && (
                          <div className="text-danger small mt-1">
                            {formik.errors.approved}
                          </div>
                        )}
                      </div>
                    )}

                    {!isAlreadyProcessed && formik.values.approved === true && (
                      <div className="col-md-12 mb-3">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            id="requireTacticRelease"
                            className="form-check-input"
                            checked={!!formik.values.requireTacticRelease}
                            onChange={(e) =>
                              formik.setFieldValue(
                                "requireTacticRelease",
                                e.target.checked
                              )
                            }
                          />
                          <label
                            className="form-check-label"
                            htmlFor="requireTacticRelease"
                          >
                            ¿Es necesario liberar automáticamente los horarios
                            conflictivos durante el período de incapacidad?
                          </label>
                        </div>
                        {formik.values.requireTacticRelease &&
                          partnerSchedule &&
                          partnerSchedule.length > 0 && (
                            <div className="alert alert-info mt-2 small">
                              <i className="fas fa-info-circle me-2"></i>
                              <strong>
                                Se eliminarán automáticamente{" "}
                                {partnerSchedule.length} horario
                                {partnerSchedule.length > 1 ? "s" : ""}
                              </strong>{" "}
                              del colaborador durante el período de incapacidad
                              aprobado.
                              <br />
                              <small className="text-muted">
                                Esto liberará las posiciones para que puedan ser
                                cubiertas por otros colaboradores durante el
                                período de incapacidad.
                              </small>
                            </div>
                          )}
                      </div>
                    )}

                                      <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Comentario del revisor{" "}
                      {formik.values.approved === false && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                    <textarea
                      className={`form-control ${
                        formik.touched.reviewerComment &&
                        formik.errors.reviewerComment
                          ? "is-invalid"
                          : ""
                      }`}
                      rows="3"
                      placeholder={"Comentario del revisor"}
                      value={formik.values.reviewerComment}
                      onChange={(e) =>
                        formik.setFieldValue(
                          "reviewerComment",
                          e.target.value
                        )
                      }
                      onBlur={formik.handleBlur}
                      name="reviewerComment"
                      disabled={isAlreadyProcessed}
                    />
                    {formik.touched.reviewerComment &&
                      formik.errors.reviewerComment && (
                        <div className="invalid-feedback">
                          {formik.errors.reviewerComment}
                        </div>
                      )}
                  </div>
                </div>
              </ConfigProvider>
            </div>
          </Panel>
        </div>

          <div
            className="card border-0 shadow-sm"
            style={{ borderRadius: "1rem", overflow: "hidden" }}
          >
            <Panel>
              <PanelHeader noButton={true} className="bg-azul">
                Archivos adjuntos de la incapacidad
              </PanelHeader>
              <IncapacityFormEditFiles
                incapacity={incapacity}
                reloadFiles={handleReloadFiles}
                isDisabled={isAlreadyProcessed}
              />
            </Panel>
          </div>
        </div>
      </div>
      </div>

      <div className="card border-0 m-4 rounded p-3">
        <div className="row justify-content-end">
          <div className="col-md-3">
            <Button
              onClick={() => navigate(-1)}
              title="Cancelar"
              variant="secondary"
              type="button"
              disabled={loading}
            />
          </div>
          {!isAlreadyProcessed ? (
            <div className="col-md-3">
              <Button
                onClick={() => formik.handleSubmit()}
                title="Procesar"
                variant="azul"
                disabled={loading}
                type="submit"
              />
            </div>
          ) : (
            <div className="col-md-3">
              <Button
                onClick={() => navigate(-1)}
                title={`Incapacidad ${getIncapacityStatus().toLowerCase()}`}
                variant="secondary"
                type="button"
                disabled={true}
              />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ApprovedIncapacity;
